/**
 * Every picture a plant ships, in the order the detail page shows them
 * (SPEC-004 D-5, SPEC-006 D-1, SPEC-009 D-4, SPEC-026 D-6).
 *
 * The hero band and the full-screen viewer are two renderings of ONE list — the
 * viewer opens on the slide the hero was showing — so the list is built here
 * rather than twice, in two components free to drift apart. The order is a
 * product decision, not a rendering detail, which is why it lives in domain.
 *
 * Alt text comes back as a message KEY and its values, never as a sentence:
 * domain has no locale, and the same picture is described in four of them.
 */

/**
 * The widest rung a CROPPED rendering is willing to pay for (SPEC-017 PR-17.7).
 *
 * The ingest now emits a third derivative at each source's own width — 1024 to
 * 1536 across the packs — so that the viewer's pinch has real pixels behind it
 * rather than an upscale. Those files are 300–900 KB, and three of the four
 * places a plant's artwork appears are not zooming into it: the browse card, the
 * companion rail and the detail hero all CROP it into a small box, and would
 * each have quietly started fetching the biggest file on the shelf.
 *
 * So the rungs are the truth about which files exist, and each rendering says
 * which of them it is a candidate for. Only the viewer offers all of them, and
 * only because someone deliberately opened it.
 */
const BAND_WIDTH = 800;

function widthSet(base, widths) {
    return widths.map((width) => `${base}-${width}.webp ${width}w`).join(', ');
}

/** The largest derivative, as the `src` an engine without `srcSet` support falls to. */
function widest(base, widths) {
    return `${base}-${widths[widths.length - 1]}.webp`;
}

/**
 * The rungs at or below a ceiling — or the smallest one there is, for a plant
 * whose art was delivered smaller than the ceiling itself. Never empty: an empty
 * `srcSet` is a picture that does not load.
 */
function upTo(widths, ceiling) {
    const affordable = widths.filter((width) => width <= ceiling);

    return affordable.length ? affordable : [widths[0]];
}

/** What a cropped rendering offers: every rung a band will pay for. */
export function bandSrcSet(base, widths) {
    return widthSet(base, upTo(widths, BAND_WIDTH));
}

export function bandSrc(base, widths) {
    return widest(base, upTo(widths, BAND_WIDTH));
}

export function plantSlides(plant) {
    const {
        illustrationBase,
        illustrationWidths,
        sceneBase,
        sceneWidths,
        leafBase,
        leafWidths,
        flowerBase,
        flowerWidths,
        rootBase,
        rootWidths,
        photos,
    } = plant?.media ?? {};

    // No specimen means no pack: every plant is drawn once, and a media block
    // without one is a half-written catalogue entry rather than a plant to show.
    if (!illustrationBase) {
        return [];
    }

    const plantName = plant.prose?.commonName ?? plant.id;

    return [
        // The scene leads where one has been drawn (SPEC-004 D-5). It answers the
        // question the cut-out specimen deliberately cannot — what does this look
        // like in a bed, next to something — and the specimen keeps the slide
        // right behind it, which is where the plant is readable on its own.
        ...(sceneBase ? [{
            key: 'scene',
            kind: 'scene',
            src: bandSrc(sceneBase, sceneWidths),
            srcSet: widthSet(sceneBase, sceneWidths),
            bandSrcSet: bandSrcSet(sceneBase, sceneWidths),
            altKey: 'plants.detail.sceneAlt',
            altValues: { plant: plantName },
        }] : []),
        {
            key: 'illustration',
            kind: 'art',
            src: bandSrc(illustrationBase, illustrationWidths),
            srcSet: widthSet(illustrationBase, illustrationWidths),
            bandSrcSet: bandSrcSet(illustrationBase, illustrationWidths),
            altKey: 'plants.detail.illustrationAlt',
            altValues: { plant: plantName },
        },
        // Third, and the widest of the close-ups: a flowering spike or a branch
        // in bloom is still a statement about the whole plant, where a leaf is
        // not (SPEC-026 D-6). It sits after the specimen for the same reason the
        // leaf does — a plant is only in flower for a few weeks of the year, and
        // the slide that says which plant this is has to come first.
        ...(flowerBase ? [{
            key: 'flower',
            kind: 'flower',
            src: bandSrc(flowerBase, flowerWidths),
            srcSet: widthSet(flowerBase, flowerWidths),
            bandSrcSet: bandSrcSet(flowerBase, flowerWidths),
            altKey: 'plants.detail.flowerAlt',
            altValues: { plant: plantName },
        }] : []),
        // Fourth, because the order is a walk inward: the bed, the plant, the
        // flower, the leaf (SPEC-009 D-4). It does not lead despite being the
        // sharpest picture on the page — a leaf filling the frame does not say
        // which plant this is, which is the first slide's job.
        ...(leafBase ? [{
            key: 'leaf',
            kind: 'leaf',
            src: bandSrc(leafBase, leafWidths),
            srcSet: widthSet(leafBase, leafWidths),
            bandSrcSet: bandSrcSet(leafBase, leafWidths),
            altKey: 'plants.detail.leafAlt',
            altValues: { plant: plantName },
        }] : []),
        // Last of the drawn takes, because it is the only picture of something
        // you cannot see (SPEC-026 D-6). A page that opened on a washed root
        // ball would be answering a question nobody had asked yet.
        ...(rootBase ? [{
            key: 'root',
            kind: 'root',
            src: bandSrc(rootBase, rootWidths),
            srcSet: widthSet(rootBase, rootWidths),
            bandSrcSet: bandSrcSet(rootBase, rootWidths),
            altKey: 'plants.detail.rootAlt',
            altValues: { plant: plantName },
        }] : []),
        ...(photos ?? []).map((photo, index) => ({
            key: photo,
            kind: 'photo',
            src: photo,
            // One rendition only: the photographs come out of the pack at a
            // single width, unlike the art this repo derives itself.
            srcSet: null,
            bandSrcSet: null,
            // The photographs ARE content — a gardener judging a plant by its
            // look needs them described, unlike the illustration, whose alt
            // repeats the heading beside it.
            altKey: 'plants.detail.photoAlt',
            altValues: { plant: plantName, index: index + 1 },
        })),
    ];
}
